import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AllowFunc, DatesSetArg, EventClickArg, EventDropArg } from '@fullcalendar/core';
import type { DateClickArg } from '@fullcalendar/interaction';
import { DateTime as DT } from 'luxon';
import type { TaskType } from '@econtractor/types';
import * as apiTask from 'api/apiTask';
import { useAuth } from 'context/AuthContext';
import { useToast } from 'context/ToastContext';
import { useTranslation } from 'react-i18next';
import { canCreateTasks } from 'utils/helpers/access/hasUserAccess';
import { useTask } from '../../TaskContext';
import type { NewTaskDefaultDue } from '../../NewTask/helpers/initNewTaskStruct';
import {
    buildTermHistoryComment,
    canEditTaskTerm,
    dueDatesFromTask,
    normalizeDueDateValues,
} from '../../shared/helpers/taskDueDateHelpers';
import {
    applyCalendarEventDrop,
    isSameNormalizedDue,
    isZeroCalendarDelta,
} from '../helpers/applyCalendarEventDrop';
import { toFullCalendarEvents } from '../helpers/toFullCalendarEvents';

export type TaskCalendarView = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay';

export type TaskCalendarCreateMenu = {
    x: number;
    y: number;
    dateMillis: number;
    allDay: boolean;
};

const dueFromDateClick = (arg: DateClickArg): NewTaskDefaultDue => {
    if (arg.allDay) {
        const parsed = DT.fromISO(arg.dateStr, { zone: 'local' });
        return {
            dateMillis: parsed.isValid ? parsed.startOf('day').toMillis() : arg.date.getTime(),
            allDay: true,
        };
    }
    return { dateMillis: arg.date.getTime(), allDay: false };
};

type TaskDateOverride = Pick<TaskType, 'dateStart' | 'dateEnd' | 'endTime' | 'multiDay' | 'withTime'>;

const useTaskCalendar = () => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { tasks, showSpinner, triggerListRefresh } = useTask();
    const { auth } = useAuth();
    const { showToast } = useToast();
    const userId = Number(auth?.user?.ID || 0);
    const canCreate = canCreateTasks(auth?.user);
    const [openTaskId, setOpenTaskId] = useState<number | null>(null);
    const [view, setView] = useState<TaskCalendarView>('dayGridMonth');
    const [viewTitle, setViewTitle] = useState('');
    const [currentDate, setCurrentDate] = useState<Date>(() => new Date());
    const [createMenu, setCreateMenu] = useState<TaskCalendarCreateMenu | null>(null);
    const [showNewTask, setShowNewTask] = useState(false);
    const [defaultDue, setDefaultDue] = useState<NewTaskDefaultDue | null>(null);
    const [dateOverrides, setDateOverrides] = useState<Record<number, TaskDateOverride>>({});
    const [dateUpdating, setDateUpdating] = useState(false);
    const tasksRef = useRef(tasks);
    const dateOverridesRef = useRef(dateOverrides);
    const savingIdsRef = useRef(new Set<number>());
    tasksRef.current = tasks;
    dateOverridesRef.current = dateOverrides;

    useEffect(() => {
        setDateOverrides({});
    }, [tasks]);

    const events = useMemo(() => {
        const list = ((tasks || []) as TaskType[]).map((task) => {
            const override = dateOverrides[Number(task.ID)];
            return override ? { ...task, ...override } : task;
        });
        return toFullCalendarEvents(list, userId);
    }, [tasks, userId, dateOverrides]);

    const hideCreateMenu = useCallback(() => {
        setCreateMenu(null);
    }, []);

    const handleDatesSet = useCallback((arg: DatesSetArg) => {
        setCreateMenu(null);
        setViewTitle(arg.view.title);
        setCurrentDate(arg.view.currentStart);
        const nextView = arg.view.type;
        if (
            nextView === 'dayGridMonth' ||
            nextView === 'timeGridWeek' ||
            nextView === 'timeGridDay'
        ) {
            setView(nextView);
        }
    }, []);

    const handleEventClick = useCallback((arg: EventClickArg) => {
        const id = Number(arg.event.id);
        setCreateMenu(null);
        if (id) setOpenTaskId(id);
    }, []);

    const handleEventAllow = useCallback<AllowFunc>((span, movingEvent) => {
        if (!movingEvent) return false;
        return span.allDay === movingEvent.allDay;
    }, []);

    const handleEventDrop = useCallback(async (arg: EventDropArg) => {
        if (isZeroCalendarDelta(arg.delta)) return;
        if (arg.event.allDay !== arg.oldEvent.allDay) {
            arg.revert();
            return;
        }

        const id = Number(arg.event.id);
        const found = ((tasksRef.current || []) as TaskType[]).find(
            (item) => Number(item.ID) === id,
        );
        const override = dateOverridesRef.current[id];
        const task = found && override ? { ...found, ...override } : found;
        if (!id || !task || !canEditTaskTerm(task, userId) || savingIdsRef.current.has(id)) {
            arg.revert();
            return;
        }

        const nextDates = applyCalendarEventDrop(task, arg.delta);
        if (!nextDates) {
            arg.revert();
            return;
        }

        const currentDates = normalizeDueDateValues(dueDatesFromTask(task));
        if (isSameNormalizedDue(currentDates, nextDates)) {
            arg.revert();
            return;
        }

        savingIdsRef.current.add(id);
        setDateUpdating(true);
        let result: Awaited<ReturnType<typeof apiTask.updateTaskDates>> = null;
        try {
            result = await apiTask.updateTaskDates(id, {
                ...nextDates,
                historyComment: buildTermHistoryComment(nextDates),
            });
        } finally {
            savingIdsRef.current.delete(id);
            setDateUpdating(false);
        }

        const updated = result?.task;
        if (!result?.stat || !updated) {
            arg.revert();
            showToast(t('common:messError'), 'danger');
            return;
        }

        setDateOverrides((prev) => ({
            ...prev,
            [id]: {
                dateStart: updated.dateStart,
                dateEnd: updated.dateEnd,
                endTime: updated.endTime,
                multiDay: updated.multiDay,
                withTime: updated.withTime,
            },
        }));
        showToast(t('messTermUpdated'), 'success');
        triggerListRefresh();
    }, [showToast, t, triggerListRefresh, userId]);

    const handleDateClick = useCallback((arg: DateClickArg) => {
        if (!canCreate) return;
        const target = arg.jsEvent.target as HTMLElement | null;
        if (target?.closest('.fc-event, .fc-more-link, .fc-popover')) return;
        const due = dueFromDateClick(arg);
        setCreateMenu({
            x: arg.jsEvent.clientX,
            y: arg.jsEvent.clientY,
            dateMillis: due.dateMillis,
            allDay: due.allDay,
        });
    }, [canCreate]);

    const openNewTaskFromMenu = useCallback(() => {
        if (!createMenu) return;
        setDefaultDue({
            dateMillis: createMenu.dateMillis,
            allDay: createMenu.allDay,
        });
        setCreateMenu(null);
        setShowNewTask(true);
    }, [createMenu]);

    const hideNewTask = useCallback(() => {
        setShowNewTask(false);
        setDefaultDue(null);
    }, []);

    const hideTaskCard = useCallback(() => {
        setOpenTaskId(null);
    }, []);

    const handleTaskCreated = useCallback(() => {
        setShowNewTask(false);
        setDefaultDue(null);
        triggerListRefresh();
    }, [triggerListRefresh]);

    return {
        tasks,
        events,
        showSpinner,
        dateUpdating,
        canCreate,
        openTaskId,
        hideTaskCard,
        view,
        setView,
        viewTitle,
        currentDate,
        createMenu,
        hideCreateMenu,
        handleDatesSet,
        handleEventClick,
        handleEventAllow,
        handleEventDrop,
        handleDateClick,
        showNewTask,
        defaultDue,
        openNewTaskFromMenu,
        hideNewTask,
        handleTaskCreated,
    };
};

export default useTaskCalendar;
